import React, { useEffect, useState } from 'react';
import {
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Modal,
  TextField,
  Theme
} from '@material-ui/core';
import {
  ModalCancelButton,
  ModalSubmitButton
} from '../parts/cloudStorage/style/style';
import GetAppIcon from '@material-ui/icons/GetApp';
import { EGmoSignatureConfirmationModal } from './EGmoSignatureConfirmationModal';
import {
  getGmoCertificationPdf,
  getGmoContractPdf,
  getDaijinGroupList,
  getDocumentStatusDocument,
  getFileDetailsById,
  getDaijinDetailInfo
} from '../../apicalls/cloud/cloudFile';
import { checkResData } from '../../utils/common';
import { useSnackbar } from 'notistack';
import { PdfIcon } from '../parts/svgIcon/FileIcon';
import { PreviewField } from './Preview';
import { CustomTooltip } from '../tooltip/CustomTooltip';
import { tooltipContents } from '../tooltip/Const';
import HelpIcon from '@material-ui/icons/Help';

type TProps = {
  setOpenESignatureModal: React.Dispatch<React.SetStateAction<boolean>>;
  constructId: number;
  eSignatureFileId?: number;
  eSignatureFileName: string;
  //   getFolderFileList: any
};

export const EGmoSignatureModal: React.FC<TProps> = (props: TProps) => {
  const {
    setOpenESignatureModal,
    constructId,
    eSignatureFileId,
    eSignatureFileName
  } = props;

  const classes = useStyles();

  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  // メッセージ
  const { enqueueSnackbar } = useSnackbar();

  // ファイル詳細用
  const [fileDetailName, setFileDetailName] = useState<string>('');
  const [fileDetailFileTypeName, setFileDetailFileTypeName] =
    useState<string>('');
  const [fileDetailExchangeDate, setFileDetailExchangeDate] =
    useState<string>('');
  const [eContractSendToName, setEContractSendToName] = useState<string>('');
  const [eContractSendToMail, setEContractSendToMail] = useState<string>('');
  const [constructName, setConstructName] = useState<string>('');
  const [envelopeStatus, setEnvelopeStatus] = useState<number>(99);
  const [identificationId, setIdentificationId] = useState<string>('');
  const [openPreview, setOpenPreview] = useState(false);
  const [openCertificationPreview, setOpenCertificationPreview] =
    useState(false);
  const [src, setSrc] = useState<string>('');
  const [certificationSrc, setCertificationSrc] = useState<string>('');
  const [fileSize, setFileSize] = useState<number>(0);

  const getFileDetail = (id: number) => {
    getFileDetailsById(id).then(resData => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        // 詳細をstateに -react hook from 実装後に不要
        setFileDetailName(data.data.name);
        setFileDetailFileTypeName(data.data.file_type_name);
        setFileDetailExchangeDate(data.data.exchange_date);
        setEContractSendToName(data.data.e_contract_send_to_name);
        setEContractSendToMail(data.data.e_contract_send_to_mail);
        setConstructName(data.data.construct_name);

        const gmo_contract_id = data.data.gmo_contract_id;
        if (gmo_contract_id) {
          setIdentificationId(gmo_contract_id);
          getDocumentStatusDocument(constructId, gmo_contract_id, id).then(
            data => {
              const getStatus = data.status.find(
                (item: any) => item.envelope_xid === gmo_contract_id
              );
              setEnvelopeStatus(Number(getStatus.envelope_status));
            }
          );
        } else {
          // 未送信
          setEnvelopeStatus(0);
        }
      });
    });
  };

  const previewPdf = () => {
    if (eSignatureFileId) {
      getGmoContractPdf(identificationId, eSignatureFileId, constructId).then(
        resData => {
          setSrc(resData.data.documents_data);
          setOpenPreview(true);
        }
      );
    }
  };

  const previewCertificationPdf = () => {
    if (eSignatureFileId) {
      getGmoCertificationPdf(
        identificationId,
        eSignatureFileId,
        constructId
      ).then(resData => {
        setCertificationSrc(resData.data.documents_data);
        setOpenCertificationPreview(true);
      });
    }
  };

  const checkForm = () => {
    let result = true;
    // 契約大臣アップロード容量制限(35MB)
    const sizeLimit = 1024 * 1024 * 35;

    const showError = (message: string) => {
      enqueueSnackbar(message, { variant: 'error' });
      result = false;
    };

    if (fileSize > sizeLimit) {
      showError('ファイルサイズは35MB以下にしてください。');
    }

    if (!eContractSendToName) {
      showError('送付先名は必須です。');
    } else if (eContractSendToName.length > 50) {
      showError('送付先名は50文字以内で入力してください。');
    }

    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!eContractSendToMail) {
      showError('送付先メールアドレスは必須です。');
    } else if (eContractSendToMail.length > 50) {
      showError('送付先メールアドレスは50文字以内で入力してください。');
    } else if (!regex.test(eContractSendToMail)) {
      showError(
        '送付先メールアドレスはメールアドレスの形式で入力してください。'
      );
    }

    setOpenConfirmation(result);
  };

  useEffect(() => {
    const fetchFileDetail = async () => {
      if (eSignatureFileId && envelopeStatus === 99) {
        await getFileDetail(eSignatureFileId);
      }
    };

    fetchFileDetail();
  }, [eSignatureFileId, envelopeStatus]);

  return (
    <>
      <Grid container style={{ backgroundColor: 'white' }}>
        <Grid item xs={12} style={{ padding: 36 }}>
          <div style={{ textAlign: 'center' }}>
            <h1 className={classes.modalTitle}>
              電子契約の送信設定（GMOサイン）
            </h1>
            <p className={classes.modalAnnotation}>
              電子契約は一度のみ送信可能です。再送が必要な場合は、資料一覧の「資料を追加」から手続きしてください。
            </p>
            <div className={classes.container}>
              <div>
                <div
                  className={classes.container}
                  style={{ margin: 10, justifyContent: 'space-between' }}
                >
                  <div className={classes.label}>工事名</div>
                  {constructName}
                </div>
                <div
                  className={classes.container}
                  style={{ margin: 10, justifyContent: 'space-between' }}
                >
                  <div className={classes.label}>書類名</div>
                  {fileDetailName}
                </div>
                <div
                  className={classes.container}
                  style={{ margin: 10, justifyContent: 'space-between' }}
                >
                  <div className={classes.label}>資料種別</div>
                  {fileDetailFileTypeName}
                </div>
                <div
                  className={classes.container}
                  style={{ margin: 10, justifyContent: 'space-between' }}
                >
                  <div className={classes.label}>日付</div>
                  {fileDetailExchangeDate}
                </div>

                <div
                  className={classes.container}
                  style={{ margin: 10, justifyContent: 'space-between' }}
                >
                  <div className={classes.label}>
                    送付先
                    <CustomTooltip
                      title={tooltipContents.EcontractDestinationName}
                      placement='right'
                      interactive
                    >
                      <HelpIcon color='action' style={{ marginLeft: 8 }} />
                    </CustomTooltip>
                  </div>
                  <TextField
                    className={`${classes.description} ${classes.customTextField}`}
                    value={eContractSendToName}
                    onChange={e => {
                      setEContractSendToName(e.target.value);
                    }}
                    variant='outlined'
                    style={{ marginTop: 10 }}
                    disabled={envelopeStatus >= 2}
                  />
                </div>
                <div className={classes.container} style={{ margin: 10 }}>
                  <div className={classes.label}>送付先メールアドレス</div>
                  <TextField
                    className={`${classes.description} ${classes.customTextField}`}
                    value={eContractSendToMail}
                    onChange={e => {
                      setEContractSendToMail(e.target.value);
                    }}
                    variant='outlined'
                    style={{ marginTop: 10 }}
                    disabled={envelopeStatus >= 2}
                  />
                </div>
                {envelopeStatus != 99 && envelopeStatus >= 2 && (
                  <>
                    <div className={classes.container} style={{ margin: 10 }}>
                      <div className={classes.label}>契約書PDF取得</div>
                      <div
                        className={`${classes.description} ${classes.customTextField}`}
                      >
                        <IconButton
                          className={classes.button}
                          onClick={() => previewPdf()}
                        >
                          <GetAppIcon />
                        </IconButton>
                      </div>
                    </div>
                    {envelopeStatus == 4 && (
                      <div className={classes.container} style={{ margin: 10 }}>
                        <div className={classes.label}>合意締結証明書取得</div>
                        <div
                          className={`${classes.description} ${classes.customTextField}`}
                        >
                          <IconButton
                            className={classes.button}
                            onClick={() => previewCertificationPdf()}
                          >
                            <PdfIcon width={21} height={26} />
                          </IconButton>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <h2 className={classes.modalTitle}>
                  電子契約送付前に、
                  <a
                    style={{ color: 'blue' }}
                    target='_blank'
                    rel='noopener noreferrer'
                    href={`https://www.notion.so/42fe3462147445deaf8afbdd06151cb9`}
                  >
                    こちら
                  </a>
                  をご確認ください。
                </h2>
              </div>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} className={classes.modalFooter}>
          <ModalCancelButton
            className={classes.button}
            variant='contained'
            onClick={() => {
              setOpenESignatureModal(false);
            }}
          >
            キャンセル
          </ModalCancelButton>
          <ModalSubmitButton
            className={classes.button}
            variant='contained'
            onClick={() => checkForm()}
            disabled={envelopeStatus >= 2}
          >
            入力内容の確認
          </ModalSubmitButton>
        </Grid>
      </Grid>
      <Modal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
      >
        <>
          {eSignatureFileId && (
            <EGmoSignatureConfirmationModal
              setOpenConfirmation={setOpenConfirmation}
              setOpenESignatureModal={setOpenESignatureModal}
              constructId={constructId}
              constructName={constructName}
              eSignatureFileId={eSignatureFileId}
              eSignatureFileName={eSignatureFileName}
              fileDetailName={fileDetailName}
              fileDetailFileTypeName={fileDetailFileTypeName}
              fileDetailExchangeDate={fileDetailExchangeDate}
              eContractSendToName={eContractSendToName}
              eContractSendToMail={eContractSendToMail}
              //   getFolderFileList={getFolderFileList}
            />
          )}
        </>
      </Modal>

      {/* プレビュー */}
      <Modal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
        open={openPreview}
        onClose={() => setOpenPreview(false)}
      >
        <PreviewField
          src={src}
          openPreview={openPreview}
          setOpenPreview={setOpenPreview}
        />
      </Modal>

      {/* プレビュー */}
      <Modal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
        open={openCertificationPreview}
        onClose={() => setOpenCertificationPreview(false)}
      >
        <PreviewField
          src={certificationSrc}
          openPreview={openCertificationPreview}
          setOpenPreview={setOpenCertificationPreview}
        />
      </Modal>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modalTitle: {
      fontWeight: 'bold',
      marginBottom: 20,
      fontSize: 16
    },
    label: {
      width: '30%',
      textAlign: 'left',
      fontSize: 14
    },
    modalFooter: {
      // モーダル下部
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderTop: '1px solid #cfcfcf',
      paddingBottom: 24,
      paddingTop: 13
    },
    container: {
      // モーダルを中央に
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    button: {
      width: 200,
      height: 48,
      borderRadius: 3
    },
    description: {
      //入力フォームラベル
      width: '70%'
    },
    customTextField: {
      '& .MuiInputBase-root': {
        height: 40,
        fontSize: 14,
        padding: '10px 26px 10px 12px'
      },
      '& .MuiOutlinedInput-input': {
        height: 'calc(100% - 20px)', // 入力部分の高さをフィールドの高さから計算
        fontSize: 14,
        padding: '10px 26px 10px 12px'
      }
    },
    modalAnnotation: {
      color: '#ff0000',
      fontSize: 12,
      marginBottom: 20,
      textAlign: 'center'
    }
  })
);
